import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import { Button, Card, CardBody, CardHeader, Divider } from '@heroui/react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AccountSelector from '@/app/components/account/account-selector.tsx';
import AccountSiteStaffMemberFormFields from '@/app/components/staff/account-site-staff-member-form-fields.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import toast from '@/app/toast.ts';
import { Heading } from '@/primitives/heading.tsx';
import { usePostStaffMemberMutation } from '@/slices/staff-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import type { NewStaffMemberModel, StaffMemberModel } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';
import StaffFormFields from './staff-form-fields.tsx';

const NewStaff = () => {
  const { currentAccount } = useSecurityContext();
  const { user } = useAuth();
  const form = useForm<NewStaffMemberModel>({
    values: {
      accountSiteStaffMember: {
        account: currentAccount,
        specialties: [],
      } as any,
    },
  });
  const navigate = useNavigate();
  const { handleSubmit, watch } = form;
  const [postStaffMember, { isLoading: isPostingStaffMember }] = usePostStaffMemberMutation();

  const accountWatch = watch('accountSiteStaffMember.account');
  const onSubmit: SubmitHandler<NewStaffMemberModel> = async (value: NewStaffMemberModel) => {
    try {
      await postStaffMember(value).unwrap();
      toast.success('Staff member saved successfully.');
      navigate('/staff');
    } catch {
      toast.error('There was an error while saving staff member.');
    }
  };

  const canManageAccounts =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      (user as StaffMemberModel)?.accountSiteStaffMembers.length > 1);

  return (
    <>
      <div className='flex items-center gap-2 mb-4'>
        <Button isIconOnly variant='flat' color='primary' onPress={() => navigate('/staff')}>
          <ArrowLeftIcon className='size-4' />
        </Button>
        <Heading>New Staff Member</Heading>
      </div>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col mt-4'>
            <StaffFormFields name='staffMember'>
              <Card shadow='sm' className='border border-gray-200'>
                <CardHeader className='flex gap-3'>
                  <h3 className='text-lg font-semibold'>Configure Initial Account</h3>
                </CardHeader>
                <Divider />
                <CardBody className='gap-4'>
                  {canManageAccounts ? (
                    <AccountSelector name='accountSiteStaffMember.account' required />
                  ) : (
                    <div className='flex flex-col gap-1'>
                      <span className='text-sm font-medium'>Account</span>
                      <p className='text-sm text-gray-600'>{accountWatch?.accountName}</p>
                    </div>
                  )}

                  <AccountSiteStaffMemberFormFields
                    name='accountSiteStaffMember'
                    data={{ account: accountWatch } as any}
                  />
                </CardBody>
              </Card>
            </StaffFormFields>
          </div>
          <div className='flex flex-row gap-5 pt-5'>
            <Button
              size='lg'
              type='submit'
              color='primary'
              variant='solid'
              isLoading={isPostingStaffMember}
            >
              Create Staff Member
            </Button>
            <Button
              size='lg'
              type='button'
              onPress={() => navigate('/staff')}
              color='default'
              variant='bordered'
            >
              Cancel
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default NewStaff;
